import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const Register = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error on change
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.firstName.trim() || form.firstName.length < 2) {
      newErrors.firstName = "Enter valid first name";
    }

    if (!form.lastName.trim()) {
      newErrors.lastName = "Last name required";
    }

    if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Enter valid email";
    }

    if (!/^(\+91[\s-]?)?\d{10}$/.test(form.phone)) {
      newErrors.phone = "Enter valid 10-digit phone number";
    }

    if (form.password.length < 6) {
      newErrors.password = "Password must be 6+ characters";
    }

    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          full_name: `${form.firstName} ${form.lastName}`,
          phone: form.phone,
        },
      },
    });

    setLoading(false);

    if (error) {
      alert(error.message);
      return;
    }

    alert("Check your email to verify your account!");
    navigate("/login");
  };

  const handleGoogleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin + "/profile",
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#f7f4f2] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-7xl grid lg:grid-cols-2 gap-20 items-center">

        {/* LEFT CONTENT */}
        <div className="hidden lg:flex flex-col justify-center">
          <div className="mb-10">
            <h2 className="text-[#b38a4d] text-5xl font-serif mb-6">
              SIYAANISH
            </h2>
            <h1 className="text-[72px] leading-[80px] font-serif text-[#0b1c14] mb-8">
              Become a
              <br />
              Patron.
            </h1>
            <p className="text-[#444] text-xl leading-10 max-w-xl">
              Join an exclusive community dedicated to slow fashion,
              bio-couture, and ancestral craftsmanship. Patrons receive
              early access to seasonal drops and our monthly printed journal.
            </p>
          </div>

          <div className="border-t border-[#ddd] pt-6 w-[420px]">
            <p className="uppercase tracking-[3px] text-xs text-[#555] mb-2">
              Est. 2024
            </p>
            <p className="text-[#555] text-lg">
              Consciously crafted in small batches.
            </p>
          </div>
        </div>

        {/* REGISTER CARD */}
        <div className="w-full bg-white border border-[#ece7e2] p-8 md:p-14">
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* NAME ROW */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[2px] text-[#444]">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="Aria"
                  className="w-full border border-[#d8d8d8] px-4 py-4 mt-3 outline-none focus:border-black"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-2">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-[2px] text-[#444]">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Vanya"
                  className="w-full border border-[#d8d8d8] px-4 py-4 mt-3 outline-none focus:border-black"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-2">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* EMAIL */}
            <div>
              <label className="text-xs uppercase tracking-[2px] text-[#444]">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="aria@gmail.com"
                className="w-full border border-[#d8d8d8] px-4 py-4 mt-3 outline-none focus:border-black"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-2">{errors.email}</p>
              )}
            </div>

            {/* PHONE */}
            <div>
              <label className="text-xs uppercase tracking-[2px] text-[#444]">
                Mobile Number
              </label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                className="w-full border border-[#d8d8d8] px-4 py-4 mt-3 outline-none focus:border-black"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-2">{errors.phone}</p>
              )}
            </div>

            {/* PASSWORD */}
            <div>
              <label className="text-xs uppercase tracking-[2px] text-[#444]">
                Create Password
              </label>
              <div className="relative mt-3">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full border border-[#d8d8d8] px-4 py-4 outline-none focus:border-black"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#444]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-2">{errors.password}</p>
              )}
            </div>

            {/* CONFIRM PASSWORD */}
            <div>
              <label className="text-xs uppercase tracking-[2px] text-[#444]">
                Confirm Password
              </label>
              <div className="relative mt-3">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full border border-[#d8d8d8] px-4 py-4 outline-none focus:border-black"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#444]"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-2">{errors.confirmPassword}</p>
              )}
            </div>

            {/* NEWSLETTER CHECKBOX */}
            <div className="flex items-start gap-4">
              <input type="checkbox" className="mt-1 w-4 h-4 cursor-pointer" />
              <p className="text-[#555] text-sm leading-7">
                Receive the "Siyaanish Letter" — a monthly curation of
                bio-textile innovation and atelier news.
              </p>
            </div>

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#062114] text-white py-5 uppercase tracking-[3px] text-sm transition ${
                loading ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
              }`}
            >
              {loading ? "Creating Account..." : "Become A Patron"}
            </button>

          </form>

          {/* DIVIDER */}
          <div className="flex items-center gap-5 my-8">
            <div className="flex-1 h-[1px] bg-[#ddd]" />
            <span className="text-sm text-[#555]">OR</span>
            <div className="flex-1 h-[1px] bg-[#ddd]" />
          </div>

          {/* GOOGLE BUTTON */}
          <button
            onClick={handleGoogleLogin}
            className="w-full border border-[#b38a4d] py-5 flex justify-center items-center gap-4 uppercase tracking-[3px] text-sm text-[#9a6d2e] hover:bg-[#fdf8f3] transition"
          >
            <img
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="google"
              className="w-5 h-5"
            />
            Continue With Google
          </button>

          {/* LOGIN LINK */}
          <p className="text-center text-sm mt-8 uppercase tracking-[1px] text-[#555]">
            Already Have An Account?{" "}
            <Link to="/login" className="underline text-black">
              Sign In
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
};

export default Register;